'use client';

import { Button } from '@/components/ui/button';
import { ChevronRight } from '@/icons/ChevronRight';
import { sendGTMEvent } from '@/lib/gtm-wrapper';
import { GTMEventType } from '@/lib/GTMEventType';
import { WebLink } from '@/lib/WebLinks';
import { BlocksIcon, Flower2Icon, SendIcon } from 'lucide-react';
import { useRouter } from 'next/navigation';
import { useEffect } from 'react';

export default function NotFoundContent() {
  const router = useRouter();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    const paths = window.location;

    sendGTMEvent({
      event: GTMEventType.EXCEPTION,
      page_url: paths.pathname || '',
      error_type: '404 Error',
      fatal: false,
    });
  }, []);

  return (
    <div className="container flex items-center justify-center px-6 py-12 mx-auto">
      <div className="w-full">
        <div className="flex flex-col items-center max-w-lg mx-auto text-center">
          <h1 className="mt-3 text-2xl font-semibold text-gray-800 dark:text-white md:text-3xl">
            Sayfa bulunamadı
          </h1>
          <p className="mt-4 text-gray-500 dark:text-gray-400">
            İstediğiniz sayfayı bulamadık, üzgünüz. Ama endişelenmeyin, sizi doğru yola
            yönlendirelim!
          </p>
          <div className="flex items-center w-full mt-6 gap-x-3 shrink-0 sm:w-auto">
            <Button
              className="flex items-center justify-center w-1/2 text-sm text-gray-700 transition-colors duration-200 bg-white border rounded-lg dark:text-gray-200 gap-x-2 sm:w-auto dark:hover:bg-gray-800 dark:bg-gray-900 hover:bg-gray-100 dark:border-gray-700"
              onClick={() => router.back()}
            >
              <ChevronRight className="rotate-180" />
              Geri
            </Button>

            <Button
              className="w-1/2 tracking-wide text-white transition-colors duration-200 bg-hzc_purple_500 rounded-lg shrink-0 sm:w-auto hover:bg-hzc_purple_600"
              onClick={() => router.replace('/')}
            >
              Anasayfa
            </Button>
          </div>
        </div>

        <div className="grid w-full max-w-6xl grid-cols-1 gap-8 mx-auto mt-8 sm:grid-cols-2 lg:grid-cols-3">
          <RedirectView
            icon={<Flower2Icon />}
            title="Tüm ürünler"
            description="Tüm ürünlerimizi keşfedin. Size özel fırsatlar burada!"
            redirectText="Ürünleri gör"
            onRedirect={() => router.replace(WebLink.PRODUCTS)}
          />
          <RedirectView
            icon={<BlocksIcon />}
            title="Anasayfa"
            description="Anasayfamıza dönün ve kategorileri keşfedin."
            redirectText="Anasayfaya Dön"
            onRedirect={() => router.replace('/')}
          />
          <RedirectView
            icon={<SendIcon />}
            title="İletişim"
            description="Bize ulaşın. Size yardımcı olmaktan mutluluk duyarız."
            redirectText="İletişime Geç"
            onRedirect={() => {}}
          />
        </div>
      </div>
    </div>
  );
}

function RedirectView({
  icon,
  title,
  description,
  redirectText,
  onRedirect,
}: {
  icon: JSX.Element;
  title: string;
  description: string;
  redirectText: string;
  onRedirect: () => void;
}) {
  return (
    <div className="p-6 rounded-lg bg-[#5E6AFF1A]">
      <span className="text-gray-500 dark:text-gray-400">{icon}</span>

      <h3 className="mt-6 font-medium text-gray-700 dark:text-gray-200 ">{title}</h3>

      <p className="mt-2 text-gray-500 dark:text-gray-400 ">{description}</p>

      <div
        onClick={onRedirect}
        className="group inline-flex items-center mt-4 text-sm text-hzc_purple_500 gap-x-2 hover:underline hover:cursor-pointer"
      >
        <span>{redirectText}</span>

        <ChevronRight className="group-hover:translate-x-2 transition-all duration-300" />
      </div>
    </div>
  );
}
